import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, ShieldCheck, ShieldOff, Trash2, RotateCcw } from "lucide-react";
import { useConfirm } from "./ConfirmDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type Role = "superadmin" | "admin" | "editor";
const ROLES: { id: Role; label: string; desc: string }[] = [
  { id: "superadmin", label: "Superadmin", desc: "Acesso total, inclusive gerenciar todos os utilizadores." },
  { id: "admin", label: "Admin", desc: "Blog, mídia, contatos recebidos e cadastro de editores." },
  { id: "editor", label: "Editor", desc: "Apenas artigos, categorias e biblioteca de mídia." },
];
const RANK: Record<string, number> = { editor: 1, admin: 2, superadmin: 3 };
const roleLabel = (r?: string) => ROLES.find((x) => x.id === r)?.label ?? r;

type AdminUser = { id: string; email: string; created_at: string; last_sign_in_at: string | null; role: Role; mfa: boolean; is_self: boolean };

const call = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke("admin-users", { body });
  if (error) {
    let msg = error.message;
    try { msg = (await (error as any).context.json()).error ?? msg; } catch { /* keep */ }
    throw new Error(msg);
  }
  return data;
};

const fmt = (d: string | null) => (d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "Nunca");

const UsersManager = ({ onGoSecurity, myRole }: { onGoSecurity: () => void; myRole: string | null }) => {
  const canAssign = (r: string) => myRole === "superadmin" || RANK[r] < RANK[myRole ?? "editor"];
  const assignable = ROLES.filter((r) => canAssign(r.id));
  const { toast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [aal2, setAal2] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ email: string; password: string; role: Role }>({ email: "", password: "", role: "editor" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data: a } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setAal2(a?.currentLevel === "aal2");
    try { setUsers((await call({ action: "list" })).users); }
    catch (e) { toast({ title: "Erro ao carregar", description: (e as Error).message, variant: "destructive" }); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const run = async (body: Record<string, unknown>, ok: string) => {
    try { await call(body); toast({ title: ok }); load(); return true; }
    catch (e) { toast({ title: "Não foi possível concluir", description: (e as Error).message, variant: "destructive" }); return false; }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const ok = await run({ action: "create", ...form }, "Utilizador cadastrado");
    setSaving(false);
    if (ok) { setOpen(false); setForm({ email: "", password: "", role: "editor" }); }
  };

  const genPw = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    const a = crypto.getRandomValues(new Uint32Array(16));
    setForm((f) => ({ ...f, password: Array.from(a, (n) => chars[n % chars.length]).join("") }));
  };

  return (
    <div className="space-y-4">
      {dialog}
      {!aal2 && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm">Para cadastrar ou remover utilizadores, ative o 2FA na sua conta e entre com o código.</p>
          <Button size="sm" onClick={onGoSecurity}>Ir para Segurança</Button>
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)}><UserPlus className="h-4 w-4 mr-2" />Novo utilizador</Button>
      </div>

      <div className="rounded-xl border border-border bg-background divide-y divide-border">
        {loading && <p className="p-6 text-sm text-muted-foreground">Carregando...</p>}
        {!loading && users.length === 0 && <p className="p-6 text-sm text-muted-foreground">Nenhum utilizador.</p>}
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-4 p-4">
            <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading font-semibold uppercase">{u.email?.[0]}</div>
            <div className="flex-1 min-w-[200px]">
              <p className="font-medium flex items-center gap-2">{u.email}{u.is_self && <Badge variant="outline">Você</Badge>}</p>
              <div className="mt-1"><Badge variant={u.role === "superadmin" ? "default" : u.role === "admin" ? "secondary" : "outline"}>{roleLabel(u.role)}</Badge></div>
              <p className="text-xs text-muted-foreground">Último acesso: {fmt(u.last_sign_in_at)} · Desde {fmt(u.created_at)}</p>
            </div>
            {u.mfa
              ? <Badge className="gap-1"><ShieldCheck className="h-3 w-3" />2FA ativo</Badge>
              : <Badge variant="outline" className="gap-1 text-muted-foreground"><ShieldOff className="h-3 w-3" />2FA pendente</Badge>}
            {!u.is_self && canAssign(u.role) && (
              <div className="flex gap-1 items-center">
                <Select value={u.role} disabled={!aal2} onValueChange={(v) => run({ action: "set_role", user_id: u.id, role: v }, "Cargo atualizado")}>
                  <SelectTrigger className="h-8 w-[130px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{assignable.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
                {u.mfa && (
                  <Button variant="ghost" size="icon" title="Redefinir 2FA (celular perdido)" disabled={!aal2}
                    onClick={async () => (await confirm({ title: "Redefinir 2FA?", description: `${u.email} precisará configurar o autenticador de novo.`, confirmLabel: "Redefinir" })) && run({ action: "reset_mfa", user_id: u.id }, "2FA redefinido")}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" title="Remover acesso" disabled={!aal2}
                  onClick={async () => (await confirm({ title: "Remover acesso ao painel?", description: `${u.email} perderá o acesso imediatamente.`, confirmLabel: "Remover", destructive: true })) && run({ action: "remove", user_id: u.id }, "Acesso removido")}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo utilizador do painel</DialogTitle>
            <DialogDescription>Envie a senha inicial por um canal seguro. Peça para a pessoa ativar o 2FA no primeiro acesso.</DialogDescription>
          </DialogHeader>
          {!aal2 ? (
            <div className="space-y-3">
              <p className="text-sm">Por segurança, só é possível cadastrar utilizadores depois de ativar a verificação em dois fatores (2FA) na sua conta e entrar com o código.</p>
              <DialogFooter><Button onClick={() => { setOpen(false); onGoSecurity(); }}>Ativar 2FA agora</Button></DialogFooter>
            </div>
          ) : (
          <form onSubmit={create} className="space-y-3">
            <Input type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <div className="flex gap-2">
              <Input placeholder="Senha inicial (mín. 10 caracteres)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={10} required />
              <Button type="button" variant="outline" onClick={genPw}>Gerar</Button>
            </div>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v as Role })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{assignable.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{ROLES.find((r) => r.id === form.role)?.desc}</p>
            <DialogFooter><Button type="submit" disabled={saving}>{saving ? "Salvando..." : "Cadastrar"}</Button></DialogFooter>
          </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManager;
